// MorrCart Tenant Help Center content.
// Plain-English, beginner-friendly how-to articles. Screenshots live in /public/kb/.
// Each article: { id, title, why, steps: [string], images: [{ src, alt }] }

export const KB_CATEGORIES = [
  {
    id: "getting-started",
    title: "Getting started",
    icon: "Rocket",
    blurb: "The basics — what MorrCart is and how customers find your store.",
    articles: [
      {
        id: "what-is-morrcart",
        title: "What is MorrCart?",
        why: "Understanding the pieces makes everything else easier.",
        steps: [
          "MorrCart is your online ordering system. Customers browse your menu, add items to a cart, and place an order — you see it come in live.",
          "You get a dashboard (this screen) to manage orders, products, customers and settings.",
          "You also get a shopping cart 'widget' that can live on your own website, and a free web address (like yourshop.morrcart.com) you can share anywhere.",
          "The tabs across the top are your main tools: Board (live orders), Register (in-person sales), Products, Customers, Loyalty, Marketing, Events and Settings.",
        ],
        images: [{ src: "/kb/kb-dashboard-tabs.png", alt: "The dashboard tabs across the top" }],
      },
      {
        id: "your-cart-address",
        title: "Your cart's web address",
        why: "This is the link you share so customers can order in one tap.",
        steps: [
          "Go to Settings → Business info.",
          "Find 'Your shopping cart address' — it looks like yourshop.morrcart.com.",
          "You can change the first part to whatever you like (letters, numbers and dashes only).",
          "Click Save.",
          "Share this link on your website, social media, texts or emails. Anyone who opens it can order right away.",
        ],
        images: [{ src: "/kb/kb-settings-info.png", alt: "Business info settings with the cart address field" }],
      },
      {
        id: "add-to-website",
        title: "Add the cart to your own website",
        why: "Let customers order without leaving your site.",
        steps: [
          "Go to Settings → Install on your website.",
          "Copy the small snippet of code shown there.",
          "Paste it into your website (most site builders have a 'custom code' or 'embed HTML' box).",
          "The floating cart button and menu will appear on your site, styled to match.",
          "Not comfortable with code? Just use your MorrCart web address instead — it works with zero setup.",
        ],
        images: [{ src: "/kb/kb-settings-install.png", alt: "Install / embed code section in Settings" }],
      },
    ],
  },
  {
    id: "taking-orders",
    title: "Taking orders (the Board)",
    icon: "LayoutGrid",
    blurb: "Watch orders arrive live and move them through your kitchen.",
    articles: [
      {
        id: "the-board",
        title: "How the live order Board works",
        why: "It's your real-time command center for every incoming order.",
        steps: [
          "Open the Board tab. Orders appear the moment a customer checks out.",
          "Each order is a card showing the customer, items and how long it's been waiting.",
          "Cards move through columns as you work them: New → Preparing → Ready → Completed.",
          "A sound plays when a new order arrives (you can mute it — see below).",
        ],
        images: [{ src: "/kb/kb-board.png", alt: "The live order board" }],
      },
      {
        id: "order-statuses",
        title: "Moving an order along (Accept, Ready, Done)",
        why: "Keeps your team in sync and shows customers accurate wait times.",
        steps: [
          "On a New order, click Accept to start preparing it.",
          "When it's made, click Ready — the customer can be notified it's ready for pickup.",
          "Click Done to move it out of the active list.",
          "Tip: you can also use keyboard shortcuts — A = accept oldest, R = ready oldest, C = complete oldest.",
        ],
        images: [{ src: "/kb/kb-order-card.png", alt: "An order card with its action buttons" }],
      },
      {
        id: "open-closed-paused",
        title: "Open, Closed & Follow store hours — your operating mode",
        why: "Tell customers whether you're taking orders right now — without ever changing your saved weekly hours.",
        steps: [
          "Open the Board tab. At the top, 'Store status for' lets you pick a location; underneath you'll see three buttons: Follow store hours, Open now, and Closed. Pick one — it changes what customers see straight away.",
          "FOLLOW STORE HOURS (your normal, everyday setting): the shop opens and closes automatically by your saved weekly hours. Customers can order when you're open and can't when you're closed — it flips over on its own. Leave it here most of the time.",
          "OPEN NOW ('I'm here'): use this when you want to take orders even though your hours say you're closed — you opened early, you're staying late, or it's a surprise busy night. Customers can order right away.",
          "CLOSED: use this to stop online orders for the rest of today even though your hours say you're open — you sold out, the kitchen's down, or you're closing early. Customers see you're closed and can't order.",
          "Important: 'Open now' and 'Closed' only last for today. Overnight they clear themselves and you go back to Follow store hours automatically — so you can never forget to flip it back in the morning.",
          "Closing for several days (a holiday or a vacation)? Don't use 'Closed' for that — instead add a holiday/closure date under Settings → Business hours (or in that location's hours). That stays closed until the date passes, all by itself.",
          "There's also a separate 'Accepting Orders / Orders are Paused' button. Use Paused when you're open but need a short breather from online orders (out to lunch, slammed on the line). Pausing always wins — even if you're set to 'Open now', pausing still stops online orders. Walk-up customers are always welcome.",
          "At a glance: a little 'Currently: …' chip at the top of your dashboard always shows your live status, so you can check it from any screen and tap it to jump to the Board.",
        ],
        images: [{ src: "/kb/kb-board-toolbar.png", alt: "Board buttons: Follow store hours, Open now, Closed" }],
      },
      {
        id: "sound-fullscreen",
        title: "Sound, Full screen & dual screens",
        why: "Set up the Board like a proper kitchen display on any monitor.",
        steps: [
          "Click 'Sound on / Muted' to toggle the new-order chime.",
          "Click 'Full screen' on the Board to open it in its own browser tab, filling the screen — perfect for a wall-mounted monitor. You stay logged in.",
          "The Register has its own 'Full screen' button that works the same way.",
          "Use 'Open Register + Board' (top of the dashboard) to pop both onto separate screens in one click.",
        ],
        images: [{ src: "/kb/kb-launcher.png", alt: "Open Register + Board launcher button" }],
      },
      {
        id: "wait-time",
        title: "The pickup wait indicator",
        why: "Customers can time their pickup so food is fresh.",
        steps: [
          "In the customer's cart, just under your hours, there's a small colored dot with a wait time.",
          "Green 'Little to no wait' means orders are going out fast.",
          "Amber '~X min wait' means you're busier right now.",
          "This is calculated automatically from how long your orders have taken this hour — you don't set it manually.",
        ],
        images: [{ src: "/kb/kb-wait.png", alt: "Wait indicator in the customer cart" }],
      },
    ],
  },
  {
    id: "locations",
    title: "Locations (shops & pop-ups)",
    icon: "MapPin",
    blurb: "Run more than one spot — a second shop, a food truck, a market stall or a weekend pop-up.",
    articles: [
      {
        id: "shops-vs-mobile",
        title: "Shops vs. mobile locations — what's the difference?",
        why: "Pick the right type so customers always know where and when they can order from you.",
        steps: [
          "MorrCart has two kinds of locations, and they behave a little differently.",
          "A SHOP (fixed location) is a bricks-and-mortar spot that's always at the same address — your café, your bakery, your second store. It's open on regular weekly hours (like Mon–Fri 8–4). Customers can find it any day it's open.",
          "A MOBILE LOCATION is anything that moves or only shows up sometimes — a food truck, a farmers-market stall, a pop-up, a catering spot. Instead of plain weekly hours, you tell it WHERE and WHEN it will be, either on a repeating schedule (e.g. 'every Saturday 9–1 at the market') or as a one-time date ('this Sunday only at the fair').",
          "Why it matters for customers: a shop stays visible whenever it's open; a mobile spot only appears while it's live or coming up soon, and it disappears again once its time has passed — so nobody tries to order from a truck that's already left.",
          "Upcoming mobile spots show in a 'Coming soon' group so customers can see where you'll be next, but they can't order until you actually go live.",
          "Both types get their own address, hours/schedule, and their own live status — and each can have its own menu and prices if you want.",
        ],
        images: [{ src: "/kb/kb-board.png", alt: "Shops and mobile locations in the dashboard" }],
      },
      {
        id: "add-a-location",
        title: "Adding an extra shop or mobile location",
        why: "Sell from more than one place under the same account, orders and customer list.",
        steps: [
          "Your main shop is always there from day one — you set it up under Settings.",
          "To add more, open the Locations tab. You'll see two groups: 'Shops' and 'Mobile Locations'.",
          "Click 'Add' in the group you want, give the location a name and address, and set its hours (for a shop) or its schedule/dates (for a mobile spot).",
          "Adding extra locations is a paid upgrade on top of your main shop. If you don't see the option, your current plan includes one location — reach out to upgrade and unlock more.",
          "Once added, a location shows up for customers automatically (mobile spots appear when they're live or coming up soon).",
        ],
        images: [{ src: "/kb/kb-board.png", alt: "Locations tab with Shops and Mobile Locations groups" }],
      },
      {
        id: "per-location-status",
        title: "Opening or closing one location at a time",
        why: "Handle each spot on its own — open the truck early without touching the café, or close one shop while the others keep taking orders.",
        steps: [
          "Open the Board tab. At the top there's a 'Store status for' picker — pick the location you want to manage (your shops are always listed; mobile locations appear when they're live now, and upcoming ones under 'Coming soon').",
          "Whatever you pick, you get the same three-way switch: Follow store hours (open/close automatically by that spot's hours), Open now (take orders there even outside its hours today), and Closed (stop orders there for today even during its hours).",
          "So each location has its own status. For example: your café runs on Follow store hours, but you tap 'Open now' on the food truck because you rolled up to a busy event, and you tap 'Closed' on your second shop because it sold out.",
          "Want the full list? Open 'Per-location availability' on the Board to see every spot with its own Follow store hours / Open now / Closed switch and a live Open/Closed badge.",
          "Just like the main shop, 'Open now' and 'Closed' for a location only last for today and reset to Follow store hours overnight. For a location that's shut for several days, add a special closure in that location's hours instead.",
          "'All locations' is a handy master switch — use it to open, close or pause every spot at once when you're opening or closing for the whole day.",
        ],
        images: [{ src: "/kb/kb-board-toolbar.png", alt: "Per-location availability with mode switches" }],
      },
    ],
  },
  {
    id: "register",
    title: "Register / POS",
    icon: "ShoppingCart",
    blurb: "Ring up walk-in customers right from the dashboard.",
    articles: [
      {
        id: "clock-in",
        title: "Clocking in with your PIN",
        why: "Ties every sale to the right employee and keeps the register secure.",
        steps: [
          "Open the Register tab. If staff PINs are set up, you'll see a list of names.",
          "Tap your name and type your 4-digit PIN to start your shift.",
          "Cashiers see a simplified, locked-down view (just Register, Board and Customers).",
          "When you're done, click Clock out to hand the register to the next person.",
          "No PINs yet? Set them up in Settings → Staff & PINs.",
        ],
        images: [{ src: "/kb/kb-register-clockin.png", alt: "Register clock-in screen with staff PINs" }],
      },
      {
        id: "build-ticket",
        title: "Building an order (ringing it up)",
        why: "The core of every in-person sale.",
        steps: [
          "Tap products on the left to add them to the ticket on the right.",
          "Use + and − to change quantities, or the trash icon to remove an item.",
          "The subtotal, tax and total update automatically as you go.",
        ],
        images: [{ src: "/kb/kb-register-ticket.png", alt: "Register with products and a ticket" }],
      },
      {
        id: "add-customer",
        title: "Adding a customer or guest",
        why: "Required before checkout — it links the sale to a person and powers loyalty.",
        steps: [
          "The 'Add customer' button turns red once you've added items — that's your reminder.",
          "Click it, then search by name, phone or email to find an existing customer.",
          "No match? Click 'Create new customer' — whatever you typed (a phone or email) drops into the right field automatically.",
          "Just need a name for the ticket? Type a guest name instead.",
          "Once attached, the customer area turns green. If you try to check out without one, a reminder pops up.",
        ],
        images: [{ src: "/kb/kb-add-customer.png", alt: "Add customer search and create dialog" }],
      },
      {
        id: "promo-loyalty-checkout",
        title: "Promo codes, rewards & checkout",
        why: "Apply discounts and finish the sale correctly.",
        steps: [
          "If the customer has an earned loyalty reward, it appears on the ticket — tap to apply it.",
          "Enter a promo code in the promo box to apply a discount.",
          "Review the total, then click 'Check out'.",
          "Choose how they're paying (e.g. pay in person) and complete the order. It appears on the Board.",
        ],
        images: [{ src: "/kb/kb-register-ticket.png", alt: "Ticket with promo and totals" }],
      },
      {
        id: "z-report",
        title: "End-of-day summary (Z-report)",
        why: "See your day's sales, tax and who sold what — without any manual work.",
        steps: [
          "Go to Settings → End-of-day report (Z-report).",
          "It builds itself automatically for today: gross sales, discounts, tax collected, total revenue, and money collected vs. still owed.",
          "It also breaks sales down by employee.",
          "Use the date picker to review any past day, and 'Print report' for a paper copy.",
        ],
        images: [{ src: "/kb/kb-zreport.png", alt: "Z-report end-of-day summary" }],
      },
    ],
  },
  {
    id: "products",
    title: "Products & menu",
    icon: "Package",
    blurb: "Build the menu customers order from.",
    articles: [
      {
        id: "add-product",
        title: "Adding a product",
        why: "Your menu is what customers actually buy.",
        steps: [
          "Open the Products tab and click 'Add product'.",
          "Enter a name, price and (optional) description.",
          "Add one or more photos — the first becomes the main image; multiple photos show as a carousel.",
          "Pick a category (like 'Drinks' or 'Pastries') to group items on your menu.",
          "Click Save. The item appears on your storefront instantly.",
        ],
        images: [{ src: "/kb/kb-product-edit.png", alt: "Add / edit product form" }],
      },
      {
        id: "categories-tax",
        title: "Categories & the Taxable toggle",
        why: "Organize the menu and charge tax correctly.",
        steps: [
          "Categories group your items into sections on the menu — set one on each product.",
          "Each product has a 'Taxable' toggle. Turn it off for tax-exempt items.",
          "Set your overall tax rate (and optional per-category rates) in Settings → Tax.",
        ],
        images: [{ src: "/kb/kb-products.png", alt: "Products list with categories" }],
      },
    ],
  },
  {
    id: "customers-loyalty",
    title: "Customers & loyalty",
    icon: "Users",
    blurb: "Keep track of regulars and reward them.",
    articles: [
      {
        id: "customer-lookup",
        title: "Finding a customer & their history",
        why: "Great service starts with knowing your regulars.",
        steps: [
          "Open the Customers tab and search by name, phone or email.",
          "Click a customer to see their orders and loyalty status.",
          "Use the email history option to see exactly which emails they've been sent.",
        ],
        images: [{ src: "/kb/kb-customers.png", alt: "Customers list and search" }],
      },
      {
        id: "loyalty-programs",
        title: "Setting up a loyalty program",
        why: "Repeat customers are your best customers — reward them automatically.",
        steps: [
          "Open the Loyalty tab and create a program (for example, 'Buy 9, get the 10th free').",
          "Choose what customers earn and the goal to unlock a reward.",
          "Customers collect automatically when they order; earned rewards show at checkout.",
          "You can run promotions and see who's close to a reward.",
        ],
        images: [{ src: "/kb/kb-loyalty.png", alt: "Loyalty program builder" }],
      },
      {
        id: "qr-signup",
        title: "QR sign-up for rewards",
        why: "Turn one-time visitors into members with a quick scan.",
        steps: [
          "In the Loyalty or Marketing area, generate a 'Scan to join' QR code.",
          "Print it or put it on a table tent / counter card.",
          "Customers scan, enter their details, and they're enrolled — and opted in for your emails.",
        ],
        images: [{ src: "/kb/kb-loyalty.png", alt: "QR sign-up for loyalty" }],
      },
    ],
  },
  {
    id: "marketing",
    title: "Marketing emails",
    icon: "Mail",
    blurb: "Reach customers who opted in.",
    articles: [
      {
        id: "email-list",
        title: "Your opt-in email list",
        why: "Email is a free, direct line to customers who want to hear from you.",
        steps: [
          "Only customers who opted in (e.g. via QR sign-up) can be emailed — this keeps you compliant.",
          "Open the Marketing tab to see your list and send messages.",
          "Order confirmations and 'ready for pickup' emails are automatic and don't require opt-in.",
        ],
        images: [{ src: "/kb/kb-marketing.png", alt: "Marketing tab with email tools" }],
      },
      {
        id: "unsubscribe",
        title: "Unsubscribe & preferences",
        why: "Respecting opt-outs protects your sender reputation (and is the law).",
        steps: [
          "Every marketing email includes an unsubscribe link automatically.",
          "When someone unsubscribes, they're removed from future sends right away.",
          "Your business name and mailing address (from Settings) appear in the footer, as required.",
        ],
        images: [{ src: "/kb/kb-marketing.png", alt: "Marketing preferences" }],
      },
    ],
  },
  {
    id: "events",
    title: "Events & catering",
    icon: "Calendar",
    blurb: "Take bookings for private events and catering.",
    articles: [
      {
        id: "event-bookings",
        title: "Taking an event / catering booking",
        why: "Capture bigger jobs with dates, details and agreements in one place.",
        steps: [
          "Turn on Events in Settings, then open the Events tab.",
          "Customers can request a booking through a guided form with a calendar.",
          "Review requests, confirm dates, and manage details and any contract/e-signature steps.",
          "An 'Book us for your event' card can appear on your storefront to invite requests.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "Events & catering bookings" }],
      },
      {
        id: "contract-setup",
        title: "Turn on contracts & write your terms",
        why: "Protect your business with a clear agreement customers sign before you hold their date.",
        steps: [
          "Open the Events tab, then go to Setup → Contracts.",
          "Turn on 'Require a signed contract' so you can send agreements for your bookings.",
          "In the 'Default contract terms' box, write your policies. Use the toolbar to add a heading, bold text, and bulleted or numbered lists — for example your payment schedule, deposit, and cancellation policy.",
          "You only write these once — they're used every time you send a contract (you can still tweak them per booking).",
          "Click Save. You can come back and edit your terms anytime.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "Contract terms setup with the rich-text editor" }],
      },
      {
        id: "contract-send",
        title: "Sending a contract for a booking",
        why: "This is how you turn an event request into a firm, signed agreement.",
        steps: [
          "Open the Events tab and find the booking — in the bookings list or on the calendar.",
          "Click 'Send contract'.",
          "Optionally enter a Quote (the total price) and a Deposit amount — these show up in the agreement.",
          "The event details are filled in for you automatically: date, time, location, guest count, the chosen package, and any menu selections and add-ons.",
          "You can adjust the terms just for this one booking, or leave them to use your saved default.",
          "Click 'Email contract'. The customer is emailed a private link to sign, and the booking's status changes to 'Contract sent'.",
          "Good to know: sending a new contract replaces any earlier unsigned one for that booking, so there's only ever one active link.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "Send contract dialog with quote, deposit and terms" }],
      },
      {
        id: "contract-customer-signs",
        title: "What your customer sees and does",
        why: "Knowing their experience helps you answer questions and set expectations.",
        steps: [
          "The customer gets an email (something like 'Your contract is ready') with a button to review and sign. If they reply to that email, it goes to your business email address.",
          "The link opens a clean page branded with your business name and colors — no account or password needed.",
          "They see the full Event Booking Agreement: a summary of their event, the menu and add-ons, the quote and deposit, and your terms.",
          "To accept, they type their full legal name, sign with a finger or mouse in the signature box, and tick the 'this is my electronic signature' checkbox.",
          "They tap 'Sign & submit'. On a phone the whole thing takes under a minute.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "The customer's branded signing page" }],
      },
      {
        id: "contract-after-signed",
        title: "After the customer signs (confirming the booking)",
        why: "A signed contract isn't the last step — you give the final go-ahead.",
        steps: [
          "The moment they sign, we create a signed PDF of the agreement (with their signature and the date) and mark the booking 'Signed — ready for confirmation'.",
          "You get an email letting you know the contract was signed.",
          "Your customer sees a 'Contract signed' confirmation with a link to download their copy, plus a note that they're booked once you give final acceptance.",
          "To finalize, open the booking and click 'Confirm & book'. This locks in the date and time — you'll get a heads-up if another confirmed event already holds that slot, or if you're over your daily event capacity.",
          "Confirming emails the customer a 'You're booked!' message with their quote and the signed contract.",
          "A booking moves through clear stages: Requested → Contract sent → Signed → Confirmed → Completed (or Cancelled). The current stage always shows on the booking.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "A signed booking ready to confirm" }],
      },
      {
        id: "contract-find",
        title: "Where to find the signed contract",
        why: "Keep the agreement one click away — for you and your customer.",
        steps: [
          "For you, a 'View signed contract' link appears in three places: the Events bookings list, the calendar booking detail, and the customer's profile.",
          "To open a customer's profile, go to the Customers tab and click their name — you'll see their bookings (each with its contract), plus their orders, spend and loyalty.",
          "Click the link anytime to open or download the signed PDF.",
          "For your customer, the signed PDF is available on the confirmation screen right after signing, in their confirmation email, and in their customer portal under that booking.",
        ],
        images: [{ src: "/kb/kb-customers.png", alt: "View signed contract from the customer profile" }],
      },
      {
        id: "contract-legal",
        title: "What's captured for your legal protection",
        why: "An e-signature only helps if it holds up — here's exactly what we record.",
        steps: [
          "The signer's typed legal name and their drawn signature image.",
          "The exact date and time they signed, plus their device info and IP address.",
          "Their acknowledgment that they agree to sign electronically — this follows the U.S. e-signature laws (ESIGN / UETA).",
          "A locked snapshot (both the on-screen version and the PDF) of exactly what they agreed to, so the terms can't be changed after the fact.",
          "A separate, tamper-evident record of the signing event, kept on file.",
          "Tip: keep your business name and mailing address current in Settings — they appear on your agreements and emails.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "What is captured when a contract is signed" }],
      },
      {
        id: "package-addons",
        title: "Choose which add-ons show on each package",
        why: "Offer tailored upsells — e.g. only show 'Extra Drink Package' on your smaller tiers.",
        steps: [
          "First create your add-ons in the Events tab under Add-ons (give each a name, description and price).",
          "Open the Events tab → Packages and edit a package.",
          "Scroll to 'Add-ons for this package'.",
          "Leave every box unchecked to offer ALL your add-ons on this package — this is the default.",
          "Or check specific add-ons to show only those on this package, so upsells feel tailored to each tier.",
          "Click 'Save package'. In the booking widget, customers see exactly the add-ons you picked, right after they build their menu.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "Add-ons for this package selector in the package editor" }],
      },
      {
        id: "package-addon-prices",
        title: "Set a different add-on price per package",
        why: "Charge the same add-on differently by tier — e.g. 'Extra Drink Package' is $150 on Mini but $120 on Plus.",
        steps: [
          "Open the Events tab → Packages and edit a package.",
          "In 'Add-ons for this package', each add-on shows its default price next to a small '$' box.",
          "Type a price in the '$' box to override that add-on's price just for this package.",
          "Leave the box blank to use the add-on's normal default price.",
          "Click 'Save package'. Customers booking this package see (and are charged) your overridden price, while every other package keeps its own.",
        ],
        images: [{ src: "/kb/kb-events.png", alt: "Per-package add-on price override field" }],
      },
    ],
  },
  {
    id: "settings",
    title: "Settings",
    icon: "Settings",
    blurb: "Set up your business details, tax, hours, branding and staff.",
    articles: [
      {
        id: "save-per-section",
        title: "Saving your settings",
        why: "Each section saves on its own — no need to scroll to the bottom.",
        steps: [
          "Settings is split into sections you expand one at a time.",
          "After changing a section, click the Save button at the bottom of that section.",
          "You'll see a confirmation that settings were updated.",
        ],
        images: [{ src: "/kb/kb-settings-info.png", alt: "A settings section with its Save button" }],
      },
      {
        id: "tax",
        title: "Setting up sales tax",
        why: "Charge the right tax so your books stay clean.",
        steps: [
          "Go to Settings → Tax and turn on 'Charge sales tax'.",
          "Enter your default rate (e.g. 8.25) and the label shown on receipts.",
          "Optionally set different rates per category (enter 0 to exempt a category).",
          "Click Save. Tax is added at checkout on taxable items.",
        ],
        images: [{ src: "/kb/kb-settings-tax.png", alt: "Tax settings" }],
      },
      {
        id: "hours",
        title: "Business hours & holidays",
        why: "Customers see when you're open, and 'Closed' shows the right reopen time.",
        steps: [
          "Go to Settings → Business hours.",
          "Set open and close times for each day, or mark a day closed.",
          "Add holiday closures for specific dates.",
          "Click Save. Today's hours show on your storefront.",
        ],
        images: [{ src: "/kb/kb-settings-hours.png", alt: "Business hours editor" }],
      },
      {
        id: "branding",
        title: "Branding (your colors)",
        why: "Make the cart feel like your brand.",
        steps: [
          "Go to Settings → Branding and pick your brand color (or a quick-pick swatch).",
          "This themes the cart button, drawers and QR codes. Button text auto-adjusts for readability.",
          "Optionally apply your color to the menu 'Order' buttons too.",
          "Click Save.",
        ],
        images: [{ src: "/kb/kb-settings-branding.png", alt: "Branding / color settings" }],
      },
      {
        id: "staff-pins",
        title: "Staff & PINs",
        why: "Give each employee a login for the register and clean sales reporting.",
        steps: [
          "Go to Settings → Staff & PINs.",
          "Add an employee with a name, a role (cashier or manager) and a 4-digit PIN.",
          "Cashiers get a locked-down register view; managers can access more.",
          "Employees clock in with their PIN on the Register tab.",
        ],
        images: [{ src: "/kb/kb-settings-staff.png", alt: "Staff and PIN management" }],
      },
      {
        id: "promo-codes",
        title: "Creating promo codes",
        why: "Run discounts and track redemptions.",
        steps: [
          "Go to Settings → Promo codes and add a code.",
          "Choose a percentage or fixed amount, and any rules (minimum spend, what it applies to).",
          "Customers enter the code in the cart; staff can enter it on the Register.",
          "Redemptions show up in your Z-report.",
        ],
        images: [{ src: "/kb/kb-settings-promos.png", alt: "Promo code builder" }],
      },
    ],
  },
];
